/*eslint-disable linebreak-style */
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../../../api/axios';
import APIEndpoints from '../../../api/APIEndpoints';
import CookiesService from '../../../shared/CookiesService';
import { HttpSuccessCodes } from '../../../errorhandling/HttpErrorCodes';
import { CONTENT_PAGE_TITLES } from '../../../../constants';
import * as yup from 'yup';
import parse from 'html-react-parser';

const useAccountSetup = () => {
  const formFields = {
    Oid: '',
    UserType: '',
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    password: '',
    ForcePasswordReset: false,
    Status: 'Active',
    SuperUser: '',
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [skeletonLoader, setSkeletonLoader] = useState(false);
  const [passwordRules, setPasswordRules] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userData, setUserData] = useState(formFields);
  const [formData, setFormData] = useState(formFields);

  const [disclaimerContent, setDisclaimerContent] = useState('');
  const [privacyPolicyContent, setPrivacyPolicyContent] = useState('');
  const [termsConditionContent, setTermsConditionContent] = useState('');
  const [selectedQuestionValues, setSelectedQuestionValues] = useState([
    null,
    null,
    null,
  ]);
  const [isButtonDisable, setIsButtonDisable] = useState(true);

  const Rules = {
    passMinLength: passwordRules.PasswordMinLength,
    passMaxLength: passwordRules.PasswordMaxLength,
    passNumericsReq: passwordRules.NoOfNumericsReqd,
    passSpecialCharReq: passwordRules.NoOfSpecialCharactersReqd,
    passUpperCaseReq: passwordRules.NoOfUpperCaseReqd,
    passLowerCaseReq: true,
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const schema = yup.object({
    firstName: yup
      .string()
      .max(35, ' Enter maximum 35 characters only')
      .required(' Enter First Name'),
    middleName: yup.string().max(25, ' Enter maximum 25 characters only'),
    lastName: yup
      .string()
      .max(60, ' Enter maximum 60 characters only')
      .required(' Enter Last Name'),
    email: yup
      .string()
      .email('Enter Valid Email Address')
      .required('Email Required'),
    confirmEmail: yup
      .string()
      .required('Confirm Email Required')
      .when('email', {
        is: (val) => (val && val.length > 0 ? true : false),
        then: yup
          .string()
          .oneOf([yup.ref('email')], 'Both Emails must be the same'),
      }),
  });

  const regexSpecialChar = new RegExp(
    /^((?=.*[-!$%^&*()/_\\+|~='"\]`{}:[;<>?,.@#]){1}).*$/,
  );

  const passwordSchema = yup.object({
    password: yup
      .string()
      .test((value, context) => {
        const { createError } = context;

        if (!value) {
          return createError({
            message: 'The Password field is required',
          });
        } else if (
          Rules.passMaxLength >= 1 &&
          !formData.SuperUser &&
          value.length > Rules.passMaxLength
        ) {
          return createError({
            message: `Enter a maximum of ${Rules.passMaxLength} characters only`,
          });
        } else if (
          Rules.passMinLength >= 1 &&
          value.length < (formData.SuperUser ? 12 : Rules.passMinLength)
        ) {
          return createError({
            message: `Enter a minimum of ${
              formData.SuperUser ? 12 : Rules.passMinLength
            } characters only`,
          });
        } else if (
          Rules.passUpperCaseReq >= 1 &&
          value.match('(?=.*[A-Z])') === null
        ) {
          return createError({
            message: formData?.SuperUser
              ? 'Enter at least one uppercase letter'
              : 'Please consult password rules',
          });
        } else if (
          Rules.passSpecialCharReq >= 1 &&
          !regexSpecialChar.test(value)
        ) {
          return createError({
            message: formData?.SuperUser
              ? 'Enter at least one special character'
              : 'Please consult password rules',
          });
        } else if (
          Rules.passNumericsReq >= 1 &&
          value.match('(?=.*[0-9])') === null
        ) {
          return createError({
            message: formData?.SuperUser
              ? 'Enter at least one numeric letter'
              : 'Please consult password rules',
          });
        } else {
          return true;
        }
      })
      .required('Enter Password'),

    confirmPassword: yup
      .string()
      .required('Enter Confirm Password')
      .oneOf([yup.ref('password')], 'Passwords do not match'),
  });

  const getPasswordRules = async () => {
    setSkeletonLoader(true);
    const responseConfiguration = await axiosInstance.get(
      APIEndpoints.getAdminConfigResources(),
    );
    const adminConfig = responseConfiguration.data;
    setPasswordRules(adminConfig?.Response?.PasswordRules);
    setSkeletonLoader(false);
  };

  const securityQuestionschema = yup.object({
    question1: yup
      .object()
      .required('Question selection is required')
      .nullable(),
    question2: yup
      .object()
      .required('Question selection is required')
      .nullable(),
    question3: yup
      .object()
      .required('Question selection is required')
      .nullable(),
    answer1: yup
      .string()
      .required('Answer is required')
      .min(4, 'Answer must be at least 4 characters')
      .max(255, 'Answer must not exceed 255 characters'),
    answer2: yup
      .string()
      .required('Answer is required')
      .min(4, 'Answer must be at least 4 characters')
      .max(255, 'Answer must not exceed 255 characters'),
    answer3: yup
      .string()
      .required('Answer is required')
      .min(4, 'Answer must be at least 4 characters')
      .max(255, 'Answer must not exceed 255 characters'),
    customQuestion1: yup.string().when('question1', {
      is: (question1) => question1 && question1.value === 'Others',
      then: () =>
        yup
          .string()
          .required('Question is required')
          .min(10, 'Question must be at least 10 characters')
          .max(99, 'Question must not exceed 99 characters'),
    }),
    customQuestion2: yup.string().when('question2', {
      is: (question2) => question2 && question2.value === 'Others',
      then: () =>
        yup
          .string()
          .required('Question is required')
          .min(10, 'Question must be at least 10 characters')
          .max(99, 'Question must not exceed 99 characters'),
    }),
    customQuestion3: yup.string().when('question3', {
      is: (question3) => question3 && question3.value === 'Others',
      then: () =>
        yup
          .string()
          .required('Question is required')
          .min(10, 'Question must be at least 10 characters')
          .max(99, 'Question must not exceed 99 characters'),
    }),
  });

  const getPageContent = async () => {
    try {
      const getLinks = await axiosInstance.get(APIEndpoints.linksResource());
      const disclaimerPage = await getLinks?.data?.Response?.Links?.filter(
        (item) => item.LinkTitle === CONTENT_PAGE_TITLES.regDisclaimer,
      );
      const privacyPage = await getLinks?.data?.Response?.Links?.filter(
        (item) => item.LinkTitle === CONTENT_PAGE_TITLES.regPrivacyPolicy,
      );
      const termsPage = await getLinks?.data?.Response?.Links?.filter(
        (item) => item.LinkTitle === CONTENT_PAGE_TITLES.regTermsAndConditions,
      );

      const disclaimerResponse = await axiosInstance.get(
        APIEndpoints.pageResource() + '/oid/' + disclaimerPage[0]['PageOID'],
      );
      const privacyResponse = await axiosInstance.get(
        APIEndpoints.pageResource() + '/oid/' + privacyPage[0]['PageOID'],
      );
      const termsResponse = await axiosInstance.get(
        APIEndpoints.pageResource() + '/oid/' + termsPage[0]['PageOID'],
      );

      const disclaimerContentHTML = parse(
        JSON.parse(disclaimerResponse?.data.Response?.PageContent),
      );
      const privacyContentHTML = parse(
        JSON.parse(privacyResponse?.data.Response?.PageContent),
      );
      const termsContentHTML = parse(
        JSON.parse(termsResponse?.data.Response?.PageContent),
      );

      setDisclaimerContent(disclaimerContentHTML);
      setPrivacyPolicyContent(privacyContentHTML);
      setTermsConditionContent(termsContentHTML);
    } catch (e) {
      return;
    }
  };
  const handleInput = (e, setFieldValue, handleChange) => {
    handleChange(e);

    setFormData({ ...formData, [e.currentTarget.name]: e.currentTarget.value });
    setFieldValue(e.currentTarget.name, e.currentTarget.value);
  };

  const handleQuestionValChange = (option, index) => {
    const newQuestionVals = selectedQuestionValues;
    newQuestionVals[index] = option;
    setSelectedQuestionValues([...newQuestionVals]);
    setIsButtonDisable(false);
  };

  const updateUser = async () => {
    const payLoad = {
      UpdateUser: {
        Oid: formData.Oid,
        UserType: formData.UserType,
        FirstName: formData.firstName,
        LastName: formData.lastName,
        UserID: formData.email,
        Password: formData.password,
        ForcePasswordReset: false,
        Status: 'Active',
        SuperUser: formData.SuperUser,
      },
    };

    try {
      const response = await axiosInstance.put(
        APIEndpoints.getUpdateUserAPIResource(),
        payLoad,
      );
      if (
        response.status === HttpSuccessCodes.HTTP_SUCCESS_CODE_200 ||
        response.status === HttpSuccessCodes.HTTP_SUCCESS_CODE_201
      ) {
        localStorage.setItem('pwd-reset', true);
        return new Promise((resolve) => {
          resolve('Success!');
        });
      } else {
        return new Promise((resolve, reject) => {
          reject('We are unable to update the details. Please try again!');
        });
      }
    } catch (e) {
      return new Promise((resolve, reject) => {
        reject(e);
      });
    }
  };

  const getStepTitle = () => {
    return currentStep === 2
      ? 'Create Password'
      : currentStep === 3
      ? 'Setup Security Questions'
      : currentStep === 4
      ? 'Privacy Policy'
      : currentStep === 5
      ? 'Terms And Conditions'
      : 'Account Setup';
  };

  useEffect(() => {
    getPasswordRules();
    getPageContent();

    setUserData({
      ...userData,
      firstName: CookiesService.get('Firstname'),
      middleName:
        CookiesService.get('Middlename') !== 'null'
          ? CookiesService.get('Middlename')
          : '',
      lastName: CookiesService.get('Lastname'),
      email: CookiesService.get('UserID'),
      confirmEmail: CookiesService.get('UserID'),
    });

    setFormData({
      ...formData,
      Oid: CookiesService.get('Oid'),
      UserType: CookiesService.get('UserType'),
      firstName: CookiesService.get('Firstname'),
      middleName:
        CookiesService.get('Middlename') !== 'null'
          ? CookiesService.get('Middlename')
          : '',
      lastName: CookiesService.get('Lastname'),
      email: CookiesService.get('UserID'),
      confirmEmail: CookiesService.get('UserID'),
      SuperUser: CookiesService.get('Superuser'),
    });
  }, []);

  return {
    currentStep,
    schema,
    passwordSchema,
    securityQuestionschema,
    skeletonLoader,
    passwordRules,
    showPassword,
    showConfirmPassword,
    selectedQuestionValues,
    userData,
    formData,
    disclaimerContent,
    privacyPolicyContent,
    termsConditionContent,
    isButtonDisable,
    setShowConfirmPassword,
    setShowPassword,
    handleQuestionValChange,
    nextStep,
    prevStep,
    handleInput,
    updateUser,
    getStepTitle,
  };
};

export default useAccountSetup;
